// src/components/groups/CreateGroup.js
// ✅ MUI PROFESSIONAL CREATE / EDIT GROUP (Responsive + Easy UX)

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useGetGroupByIdQuery,
  useGetAllGroupsQuery,
} from "../../redux/admin/groupApi";

import { toast } from "react-hot-toast";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Tooltip,
  CircularProgress,
  Skeleton,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { ArrowLeft, Save, Palette, Folder, Sparkles } from "lucide-react";

const GROUP_TYPES = [
  { value: "fleet", label: "Fleet (Vahan Samuh)", icon: "🚗" },
  { value: "sub_fleet", label: "Sub Fleet", icon: "🚐" },
  { value: "region", label: "Region (Kshetra)", icon: "🌍" },
  { value: "department", label: "Department (Vibhag)", icon: "🏢" },
  { value: "client_group", label: "Client Group", icon: "👥" },
  { value: "custom", label: "Custom", icon: "📁" },
];

const PRESET_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#F97316",
  "#6366F1",
  "#14B8A6",
];

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const CreateGroup = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"));

  const isEditMode = Boolean(id);

  const [form, setForm] = useState({
    name: "",
    description: "",
    groupType: "fleet",
    color: "#3B82F6",
    parentGroup: "",
    settings: {
      allowSubGroups: true,
      maxDevices: 1000,
      maxSubGroups: 50,
      autoAssignNewDevices: false,
      notifyOnDeviceAdd: true,
      notifyOnDeviceRemove: true,
    },
  });

  const [errors, setErrors] = useState({});

  // ✅ Fetch existing group (edit mode)
  const { data: groupData, isLoading: isLoadingGroup, error: groupError } =
    useGetGroupByIdQuery(id, { skip: !isEditMode });

  // ✅ Fetch groups for parent selection
  const { data: allGroupsData } = useGetAllGroupsQuery({ limit: 100 });
  const parentGroups = useMemo(() => {
    const arr = allGroupsData?.data || [];
    return arr.filter((g) => g._id !== id);
  }, [allGroupsData, id]);

  // ✅ Mutations
  const [createGroup, { isLoading: isCreating }] = useCreateGroupMutation();
  const [updateGroup, { isLoading: isUpdating }] = useUpdateGroupMutation();
  const isSubmitting = isCreating || isUpdating;

  // ✅ Load edit data into form
  useEffect(() => {
    if (groupData?.data && isEditMode) {
      const g = groupData.data;
      setForm({
        name: g.name || "",
        description: g.description || "",
        groupType: g.groupType || "fleet",
        color: g.color || "#3B82F6",
        parentGroup: g.parentGroup?._id || g.parentGroup || "",
        settings: {
          allowSubGroups: g.settings?.allowSubGroups ?? true,
          maxDevices: g.settings?.maxDevices ?? 1000,
          maxSubGroups: g.settings?.maxSubGroups ?? 50,
          autoAssignNewDevices: g.settings?.autoAssignNewDevices ?? false,
          notifyOnDeviceAdd: g.settings?.notifyOnDeviceAdd ?? true,
          notifyOnDeviceRemove: g.settings?.notifyOnDeviceRemove ?? true,
        },
      });
    }
  }, [groupData, isEditMode]);

  // ✅ Validation
  const validate = () => {
    const e = {};

    const name = form.name.trim();
    if (!name) e.name = "Group name is required";
    else if (name.length < 2) e.name = "Name must be at least 2 characters";
    else if (name.length > 100) e.name = "Name cannot exceed 100 characters";

    if (form.description && form.description.length > 500) {
      e.description = "Description cannot exceed 500 characters";
    }

    // numeric sanity
    if (Number.isNaN(form.settings.maxDevices) || form.settings.maxDevices < 1) {
      e.maxDevices = "Max devices must be at least 1";
    }
    if (
      Number.isNaN(form.settings.maxSubGroups) ||
      form.settings.maxSubGroups < 0
    ) {
      e.maxSubGroups = "Max sub-groups must be 0 or more";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        groupType: form.groupType,
        color: form.color,
        settings: {
          ...form.settings,
          maxDevices: clamp(Number(form.settings.maxDevices || 0), 1, 10000),
          maxSubGroups: clamp(Number(form.settings.maxSubGroups || 0), 0, 1000),
        },
        ...(form.parentGroup ? { parentGroup: form.parentGroup } : {}),
      };

      if (isEditMode) {
        await updateGroup({ id, ...payload }).unwrap();
        toast.success("Group updated successfully!");
      } else {
        await createGroup(payload).unwrap();
        toast.success("Group created successfully!");
      }

      navigate("/groups");
    } catch (err) {
      console.error(err);
      toast.error(err?.data?.msg || "Something went wrong");
    }
  };

  const typeLabel = useMemo(() => {
    const t = GROUP_TYPES.find((x) => x.value === form.groupType);
    return t ? `${t.icon} ${t.label}` : form.groupType;
  }, [form.groupType]);

  const headerTitle = isEditMode ? "✏️ Edit Group" : "📁 Create New Group";

  // ✅ Loading / Error (edit mode)
  if (isEditMode && isLoadingGroup) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          }}
        >
          <Stack spacing={2}>
            <Skeleton width="40%" height={36} />
            <Skeleton width="70%" height={24} />
            <Skeleton variant="rounded" height={160} />
            <Skeleton variant="rounded" height={220} />
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (isEditMode && groupError) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          }}
        >
          <Alert
            severity="error"
            action={
              <Button onClick={() => navigate("/groups")} size="small">
                Back
              </Button>
            }
          >
            Error loading group details.
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={1.5}
      >
        <Stack spacing={0.5}>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate("/groups")}
            sx={{ borderRadius: 2, width: "fit-content" }}
            disabled={isSubmitting}
          >
            Back to Groups
          </Button>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="h5" fontWeight={900}>
              {headerTitle}
            </Typography>
            <Chip
              icon={<Folder size={16} />}
              label={typeLabel}
              variant="outlined"
              sx={{ borderRadius: 2, fontWeight: 800 }}
            />
          </Stack>

          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Fill basic details, choose appearance color, and configure settings.
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            onClick={() => navigate("/groups")}
            sx={{ borderRadius: 2, fontWeight: 800 }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                <Save size={18} />
              )
            }
            sx={{ borderRadius: 2, fontWeight: 900, px: 2.2, py: 1.1 }}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Saving…"
              : isEditMode
              ? "Update Group"
              : "Create Group"}
          </Button>
        </Stack>
      </Stack>

      {/* Main Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        }}
      >
        <Grid container spacing={2.2}>
          {/* LEFT: Basic Info */}
          <Grid item xs={12} md={7}>
            <Stack spacing={2}>
              <Stack spacing={0.6}>
                <Typography fontWeight={900} sx={{ color: "text.primary" }}>
                  Basic Information
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Name, description, type and parent group.
                </Typography>
              </Stack>

              <TextField
                label="Group Name *"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                error={Boolean(errors.name)}
                helperText={errors.name || " "}
                fullWidth
                size="small"
                inputProps={{ maxLength: 100 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              <TextField
                label="Description (Optional)"
                value={form.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
                error={Boolean(errors.description)}
                helperText={
                  errors.description
                    ? errors.description
                    : `${form.description.length}/500 characters`
                }
                fullWidth
                size="small"
                multiline
                rows={3}
                inputProps={{ maxLength: 500 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Group Type"
                    value={form.groupType}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, groupType: e.target.value }))
                    }
                    fullWidth
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  >
                    {GROUP_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.icon} {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Parent Group (Optional)"
                    value={form.parentGroup}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, parentGroup: e.target.value }))
                    }
                    fullWidth
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    helperText="Select a parent to create a sub-group"
                  >
                    <MenuItem value="">
                      <em>None (Root Level)</em>
                    </MenuItem>
                    {parentGroups.map((g) => (
                      <MenuItem key={g._id} value={g._id}>
                        {g.name} {g.groupCode ? `(${g.groupCode})` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Stack>
          </Grid>

          {/* RIGHT: Preview + Appearance */}
          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              <Stack spacing={0.6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Palette size={18} />
                  <Typography fontWeight={900}>Appearance</Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Pick a color for quick visual identification.
                </Typography>
              </Stack>

              {/* Preview Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                  background: alpha(form.color, 0.06),
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: 2.5,
                      display: "grid",
                      placeItems: "center",
                      background: form.color,
                      color: "white",
                      flexShrink: 0,
                    }}
                  >
                    <Folder size={20} />
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={900} noWrap>
                      {form.name?.trim() || "Group Name Preview"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                      noWrap
                    >
                      {typeLabel}
                    </Typography>
                  </Box>

                  <Chip
                    icon={<Sparkles size={16} />}
                    label={form.color}
                    variant="outlined"
                    sx={{ borderRadius: 2, fontWeight: 800 }}
                  />
                </Stack>
              </Paper>

              {/* Color Picker */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                }}
              >
                <Typography fontWeight={800} sx={{ mb: 1 }}>
                  Group Color
                </Typography>

                <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                  {PRESET_COLORS.map((c) => {
                    const selected = form.color === c;
                    return (
                      <Tooltip title={c} key={c}>
                        <IconButton
                          onClick={() => setForm((p) => ({ ...p, color: c }))}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2.2,
                            background: c,
                            border: selected
                              ? `3px solid ${alpha("#0f172a", 0.85)}`
                              : `2px solid ${alpha("#000", 0.08)}`,
                            "&:hover": { transform: "scale(1.06)" },
                            transition: "0.15s ease",
                          }}
                        />
                      </Tooltip>
                    );
                  })}

                  {/* Custom color */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2.2,
                      border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
                      display: "grid",
                      placeItems: "center",
                      overflow: "hidden",
                      position: "relative",
                    }}
                    title="Custom color"
                  >
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, color: e.target.value }))
                      }
                      style={{
                        width: 60,
                        height: 60,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    />
                  </Box>
                </Stack>

                <FormHelperText sx={{ mt: 1 }}>
                  Tip: Use different colors for different departments/regions.
                </FormHelperText>
              </Paper>
            </Stack>
          </Grid>

          {/* Settings */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              <Typography fontWeight={900}>⚙️ Group Settings</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Enable/disable features and set limits.
              </Typography>

              <Grid container spacing={2}>
                {/* Switches */}
                <Grid item xs={12} md={7}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    }}
                  >
                    <Stack spacing={0.8}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.settings.allowSubGroups}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                settings: {
                                  ...p.settings,
                                  allowSubGroups: e.target.checked,
                                },
                              }))
                            }
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight={800}>
                              Allow Sub-Groups
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Allow creating sub-groups under this group
                            </Typography>
                          </Box>
                        }
                      />

                      <Divider />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.settings.autoAssignNewDevices}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                settings: {
                                  ...p.settings,
                                  autoAssignNewDevices: e.target.checked,
                                },
                              }))
                            }
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight={800}>
                              Auto-Assign New Devices
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Automatically add new devices to this group
                            </Typography>
                          </Box>
                        }
                      />

                      <Divider />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.settings.notifyOnDeviceAdd}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                settings: {
                                  ...p.settings,
                                  notifyOnDeviceAdd: e.target.checked,
                                },
                              }))
                            }
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight={800}>
                              Notify on Device Add
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Send notification when device is added
                            </Typography>
                          </Box>
                        }
                      />

                      <Divider />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.settings.notifyOnDeviceRemove}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                settings: {
                                  ...p.settings,
                                  notifyOnDeviceRemove: e.target.checked,
                                },
                              }))
                            }
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight={800}>
                              Notify on Device Remove
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Send notification when device is removed
                            </Typography>
                          </Box>
                        }
                      />
                    </Stack>
                  </Paper>
                </Grid>

                {/* Numeric Limits */}
                <Grid item xs={12} md={5}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                      height: "100%",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Typography fontWeight={900}>Limits</Typography>

                      <TextField
                        type="number"
                        label="Max Devices"
                        value={form.settings.maxDevices}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            settings: {
                              ...p.settings,
                              maxDevices: Number(e.target.value),
                            },
                          }))
                        }
                        fullWidth
                        size="small"
                        error={Boolean(errors.maxDevices)}
                        helperText={errors.maxDevices || "Allowed range: 1 - 10000"}
                        inputProps={{ min: 1, max: 10000 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />

                      <TextField
                        type="number"
                        label="Max Sub-Groups"
                        value={form.settings.maxSubGroups}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            settings: {
                              ...p.settings,
                              maxSubGroups: Number(e.target.value),
                            },
                          }))
                        }
                        fullWidth
                        size="small"
                        error={Boolean(errors.maxSubGroups)}
                        helperText={
                          errors.maxSubGroups || "Allowed range: 0 - 1000"
                        }
                        inputProps={{ min: 0, max: 1000 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />

                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Limits help control large fleets & hierarchy growth.
                      </Alert>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              {/* Footer actions for mobile */}
              {isDownMd ? (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    position: "sticky",
                    bottom: 12,
                    background: alpha(theme.palette.background.paper, 0.95),
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/groups")}
                      disabled={isSubmitting}
                      sx={{ borderRadius: 2, fontWeight: 800 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={18} sx={{ color: "white" }} />
                        ) : (
                          <Save size={18} />
                        )
                      }
                      sx={{ borderRadius: 2, fontWeight: 900 }}
                    >
                      {isSubmitting
                        ? "Saving…"
                        : isEditMode
                        ? "Update"
                        : "Create"}
                    </Button>
                  </Stack>
                </Paper>
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CreateGroup;
